/**
 * The composition root.
 *
 * Import order matters and is not cosmetic. The design system's stylesheets must precede
 * the application's, or the application's overrides are the ones that lose. The theme
 * attribute is set on the document element in `index.html` rather than here, so the first
 * paint is already themed and there is no flash of unthemed content.
 *
 * Configuration is read and validated before anything renders. Invalid configuration
 * stops the application with an explanation rather than starting it in a state where the
 * failure surfaces later, on whichever screen first touches the bad value.
 */

import '@wk/components-v3-react16/all.min.css'
import '@wk/components-v3-react16/theme-PDS-v3.min.css'
import '@wk/components-v3-react16/common-classes.min.css'
import './app.css'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'

import { AuthProvider } from './platform/auth/AuthProvider'
import { RuntimeConfigError, runtimeConfig } from './platform/config/runtime'
import { createModuleRegistry } from './modules'
import { createRouter } from './platform/routing/router'

/**
 * The last-resort failure page.
 *
 * Plain DOM, no design system, no React. If the configuration is broken then nothing can
 * be assumed to work, and a failure page that itself depends on the thing that failed
 * shows the user a blank screen.
 */
function renderFatal(container: HTMLElement, message: string): void {
  container.textContent = ''
  const wrapper = document.createElement('div')
  wrapper.setAttribute('role', 'alert')
  wrapper.style.cssText =
    'padding:2rem;font-family:system-ui,sans-serif;max-width:60ch'

  const heading = document.createElement('h1')
  heading.textContent = 'This application is not configured correctly'
  const detail = document.createElement('p')
  detail.textContent = message
  const advice = document.createElement('p')
  advice.textContent =
    'This is a deployment problem rather than something you can fix here. Send the message above to whoever deployed this environment.'

  wrapper.append(heading, detail, advice)
  container.append(wrapper)
}

const container = document.getElementById('app')
if (container === null) {
  throw new Error(
    'index.html is missing the #app element the application mounts into.',
  )
}

try {
  const config = runtimeConfig()
  const registry = createModuleRegistry()
  const router = createRouter(registry, config)

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Refetching on every window focus would re-query the whole screen each time a
        // user returned from their mail client. The queries that genuinely need it opt in
        // individually; the approval queue is the one that does.
        refetchOnWindowFocus: false,
        // A request that fails because of a refusal is not retried. The per-query policy
        // decides; this is only the floor.
        retry: 1,
      },
    },
  })

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
} catch (error) {
  // A configuration failure is reported precisely. Any other boot failure is reported as
  // itself rather than being dressed up as a configuration problem.
  renderFatal(
    container,
    error instanceof RuntimeConfigError
      ? error.message
      : error instanceof Error
        ? `The application could not start: ${error.message}`
        : 'The application could not start, and the reason could not be determined.',
  )
  throw error
}
